/**
 * Paso3Confirmar.jsx
 * ─────────────────────────────────────────────────────────────
 * Paso 3: resumen de cambios antes de guardar (solo lectura).
 *
 * Muestra de forma condicional las secciones según los tipos de cambio:
 *   - Siempre: información general + tipos de cambio seleccionados
 *   - Con horarios: tabla de estructuras modificadas con indicadores de cambio
 *   - Con unidad: nueva unidad seleccionada
 *   - Con trazo: aviso de redirección al editor de trazo
 *
 * Props: { state, catalogs, modoEdicion }
 */

import { memo, useMemo } from "react";
import { Tag } from "primereact/tag";

import {getMod, CmrInfoBanner, hhmm} from "../cmrShared";
import {
    CHANGE_IDS,
    CHANGE_LABELS,
    hasChange,
    hasTrazoCambio,
    showHorarios as shouldShowHorarios,
    hasFrecuencia,
    frecuenciaModifica,
    frecuenciaGeneraNueva,
    FRECUENCIA_MODO_LABELS,
    getIdsPorAccion
} from "../hooks/ChangeTypes";

// ─── ÁTOMO: celda del resumen ─────────────────────────────────────────────────

const SummaryField = memo(({ label, children }) => (
    <div className="flex-1" style={{ minWidth: "140px" }}>
        <p className="cmr-summary-section__field-label m-0">{label}</p>
        <div className="cmr-summary-section__field-value">{children}</div>
    </div>
));
SummaryField.displayName = "SummaryField";

// ─── ÁTOMO: chip de tipo de cambio ───────────────────────────────────────────

const CHANGE_SEVERITY = {
    [CHANGE_IDS.HORARIOS]  : "info",
    [CHANGE_IDS.UNIDAD]    : "warning",
    [CHANGE_IDS.EXTENSION] : "success",
    [CHANGE_IDS.REDUCCION] : "danger",
    [CHANGE_IDS.PARADAS]   : "secondary",
};

const CHANGE_ICON = {
    [CHANGE_IDS.HORARIOS]  : "pi-clock",
    [CHANGE_IDS.UNIDAD]    : "pi-truck",
    [CHANGE_IDS.EXTENSION] : "pi-arrow-right",
    [CHANGE_IDS.REDUCCION] : "pi-arrow-left",
    [CHANGE_IDS.PARADAS]   : "pi-map-marker",
};

// ─── ÁTOMO: fila de estructura con indicadores de cambio ─────────────────────

const EstructuraResumen = memo(({ estructura, idx, modificaciones, tiposServicio }) => {
    const nombre       = getMod(modificaciones, estructura.id, "nombre",       estructura.nombre);
    const horaInicio = hhmm(getMod(modificaciones, estructura.id, "horaInicio", estructura.horaInicio));
    const horaFin    = hhmm(getMod(modificaciones, estructura.id, "horaFin",    estructura.horaFin));
    const dias         = getMod(modificaciones, estructura.id, "dias",         estructura.dias);
    const tipoServicio = getMod(modificaciones, estructura.id, "tipoServicio", estructura.tipoServicio);

    const tipoServicioId = getMod(modificaciones, estructura.id, "tipoServicio", estructura.tipoServicio);

    // Busca el objeto en el catálogo
    const servicioObj     = tiposServicio.find((s) => s.id === tipoServicioId);
    const tipoServicioNombre = servicioObj?.nombre ?? tipoServicioId; // fallback al id si no encuentra

    const servicioOrigObj    = tiposServicio.find((s) => s.id === estructura.tipoServicio);
    const tipoServicioOrigNombre = servicioOrigObj?.nombre ?? estructura.tipoServicio;


    const cambios = useMemo(() => ({
        nombre      : nombre       !== estructura.nombre,
        horaInicio: horaInicio !== hhmm(estructura.horaInicio),
        horaFin   : horaFin    !== hhmm(estructura.horaFin),
        dias        : JSON.stringify([...dias].sort()) !== JSON.stringify([...estructura.dias].sort()),
        tipoServicio: tipoServicio !== estructura.tipoServicio,
    }), [nombre, horaInicio, horaFin, dias, tipoServicio, estructura]);

    const hayCambios = Object.values(cambios).some(Boolean);

    return (
        <div className={`cmr-summary-section__row p-3 flex flex-column gap-3${idx % 2 === 0 ? " cmr-summary-section__row--alt" : ""}`}>
            {/* Cabecera */}
            <div className="flex align-items-center gap-2">
                <div
                    className="cmr-card-icon flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 28, height: 28, fontSize: "0.85rem" }}
                    aria-hidden="true"
                >
                    {idx + 1}
                </div>
                <span className="font-bold text-color">{nombre}</span>
                <Tag value={hayCambios ? "Con cambios" : "Sin cambios"} severity={hayCambios ? "info" : "success"} />
            </div>

            {/* Detalle */}
            <div className="grid pl-4">
                <div className="col-12 md:col-4">
                    <p className="cmr-summary-section__field-label m-0">Horario</p>
                    <div className="flex align-items-center gap-2">
                        <span className="cmr-summary-section__field-value">{horaInicio} – {horaFin}</span>
                        {(cambios.horaInicio || cambios.horaFin) && <Tag value="↑" severity="info" />}
                    </div>
                    {(cambios.horaInicio || cambios.horaFin) && (
                        <p className="cmr-summary-section__change-hint m-0">
                            Antes: {estructura.horaInicio} – {estructura.horaFin}
                        </p>
                    )}
                </div>

                <div className="col-12 md:col-4">
                    <p className="cmr-summary-section__field-label m-0">Días</p>
                    <div className="flex align-items-center gap-2">
                        <span className="cmr-summary-section__field-value" style={{ fontSize: "0.85rem" }}>
                            {dias.join(", ")}
                        </span>
                        {cambios.dias && <Tag value="↑" severity="info" />}
                    </div>
                    {cambios.dias && (
                        <p className="cmr-summary-section__change-hint m-0">
                            Antes: {estructura.dias.join(", ")}
                        </p>
                    )}
                </div>

                <div className="col-12 md:col-4">
                    <p className="cmr-summary-section__field-label m-0">Tipo de servicio</p>
                    <div className="flex align-items-center gap-2">
                        <span className="cmr-summary-section__field-value">{tipoServicioNombre}</span>
                        {cambios.tipoServicio && <Tag value="↑" severity="info" />}
                    </div>
                    {cambios.tipoServicio && (
                        <p className="cmr-summary-section__change-hint m-0">
                            Antes: {tipoServicioOrigNombre}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
});
EstructuraResumen.displayName = "EstructuraResumen";

// ─── PASO 3 ───────────────────────────────────────────────────────────────────

const Paso3Confirmar = memo(({ state, catalogs, modoEdicion }) => {
    const {
        clienteId, ticketId, rutaId,
        changeType, estructurasSeleccionadas,
        nuevaUnidad, modificaciones,
        frecuenciaModo, estructurasCopiadas
    } = state;

    // ── Flags de visibilidad ──────────────────────────────────────────────────
    const showHorarioSection = shouldShowHorarios(changeType);
    const showUnidadSection  = hasChange(changeType, CHANGE_IDS.UNIDAD);
    const showTrazoNotice    = hasTrazoCambio(changeType);
    const showFrecuencia     = hasFrecuencia(changeType);
    const frecModifica       = showFrecuencia && frecuenciaModifica(frecuenciaModo);
    const frecNueva          = showFrecuencia && frecuenciaGeneraNueva(frecuenciaModo);
    const { idsAEditar } = getIdsPorAccion(state);


    // ── Datos resueltos del catálogo ──────────────────────────────────────────
    const cliente       = useMemo(() => catalogs.clientes.data.find((c) => c.id === clienteId),       [catalogs.clientes.data, clienteId]);
    const ticket        = useMemo(() => catalogs.tickets.data.find((t) => t.id === ticketId),         [catalogs.tickets.data, ticketId]);
    const ruta          = useMemo(() => catalogs.rutas.data.find((r) => r.id === rutaId),             [catalogs.rutas.data, rutaId]);
    const nuevaUnidadObj = useMemo(() => catalogs.tiposVehicle.data.find((v) => v.ID === nuevaUnidad), [catalogs.tiposVehicle.data, nuevaUnidad]);

    const estructurasAModificar = ruta?.estructuras.filter((e) => idsAEditar.includes(e.id)) ?? [];

    return (
        <>
            <CmrInfoBanner severity={modoEdicion ? "warn" : "success"}>
                {modoEdicion
                    ? <><strong>¡Revise los cambios!</strong> Verifique la información antes de actualizar la cotización.</>
                    : <><strong>Paso 3: Confirmación.</strong> Revisa el resumen antes de guardar la cotización de modificación.</>
                }
            </CmrInfoBanner>

            {/* ── Aviso de redirect al trazo ───────────────────────────── */}
            {showTrazoNotice && (
                <CmrInfoBanner severity="warn">
                    <strong>Acción requerida post-guardado:</strong> al hacer clic en
                    {modoEdicion ? " «Actualizar Cotización»" : " «Guardar y Editar Trazo»"},
                    serás redirigido automáticamente al <strong>editor de trazo</strong> para
                    completar el recorrido en el mapa.
                </CmrInfoBanner>
            )}

            {/* ── Información general ──────────────────────────────────── */}
            <section className="cmr-summary-section mb-4" aria-labelledby="cmr-summary-general">
                <div className="cmr-summary-section__header flex align-items-center gap-2 p-3">
                    <i className="pi pi-file" aria-hidden="true" />
                    <span id="cmr-summary-general" className="font-bold text-color">Información General</span>
                </div>

                {/* Fila 1: datos de la cotización */}
                <div className="cmr-summary-section__row flex flex-wrap gap-4 p-3">
                    <SummaryField label="Cliente">   {cliente?.nombre || "—"} </SummaryField>
                    <SummaryField label="Ticket">    {ticket?.nombre  || "—"} </SummaryField>
                    <SummaryField label="Ruta">      {ruta?.nombre    || "—"} </SummaryField>
                    <SummaryField label="Tipo ruta"> {ruta?.tipoRuta  || "—"} </SummaryField>
                </div>

                {/* Fila 2: tipos de cambio seleccionados */}
                <div className="cmr-summary-section__row flex flex-wrap gap-4 p-3">
                    <SummaryField label="Tipos de cambio">
                        <div className="flex flex-wrap gap-2 mt-1">
                            {changeType.length > 0
                                ? changeType.map((id) => (
                                    <Tag
                                        key={id}
                                        icon={`pi ${CHANGE_ICON[id] ?? "pi-tag"}`}
                                        value={CHANGE_LABELS[id] ?? `Cambio ${id}`}
                                        severity={CHANGE_SEVERITY[id] ?? "info"}
                                    />
                                ))
                                : <span className="text-color-secondary">—</span>
                            }
                        </div>
                    </SummaryField>
                </div>

                {/* Fila 3: modo de frecuencia (solo si aplica) */}
                {showFrecuencia && frecuenciaModo && (
                    <div className="cmr-summary-section__row flex flex-wrap gap-4 p-3">
                        <SummaryField label="Modo de frecuencia">
                            <Tag
                                icon="pi pi-sliders-h"
                                value={FRECUENCIA_MODO_LABELS[frecuenciaModo]}
                                severity="info"
                            />
                        </SummaryField>
                    </div>
                )}
            </section>

            {/* ── Cambio de unidad ─────────────────────────────────────── */}
            {showUnidadSection && (
                <section className="cmr-summary-section mb-4" aria-labelledby="cmr-summary-unidad">
                    <div className="cmr-summary-section__header flex align-items-center gap-2 p-3">
                        <i className="pi pi-truck" aria-hidden="true" />
                        <span id="cmr-summary-unidad" className="font-bold text-color">Cambio de Unidad</span>
                    </div>
                    <div className="cmr-summary-section__row flex flex-wrap gap-4 p-3">
                        <SummaryField label="Unidad actual">
                            {ruta?.unidadActual.nombre}
                        </SummaryField>
                        <SummaryField label="Nueva unidad">
                            <div className="flex align-items-center gap-2">
                                <span>{nuevaUnidadObj ? nuevaUnidadObj.TIPO : "Sin cambio"}</span>
                                <Tag
                                    value={nuevaUnidadObj ? "Cambiada" : "Sin cambio"}
                                    severity={nuevaUnidadObj ? "info" : "success"}
                                />
                            </div>
                        </SummaryField>
                    </div>
                </section>
            )}

            {/* ── Estructuras con horarios modificados ─────────────────── */}
            {(showHorarioSection || frecModifica) && estructurasAModificar.length > 0 && (
                <section className="cmr-summary-section mb-4" aria-labelledby="cmr-summary-estructuras">
                    <div className="cmr-summary-section__header flex align-items-center gap-2 p-3">
                        <i className="pi pi-clock" aria-hidden="true" />
                        <span id="cmr-summary-estructuras" className="font-bold text-color">
                            Estructuras Modificadas
                        </span>
                        <Tag value={String(estructurasAModificar.length)} severity="info" />
                    </div>

                    {estructurasAModificar.map((est, idx) => (
                        <EstructuraResumen
                            key={est.id}
                            estructura={est}
                            idx={idx}
                            modificaciones={modificaciones}
                            tiposServicio={catalogs.tiposServicio.data}
                        />
                    ))}
                </section>
            )}

            {/* ── Estructuras copiadas (frecuencia modo "nueva" o "ambas") ── */}
            {frecNueva && estructurasCopiadas.length > 0 && (
                <section className="cmr-summary-section mb-4" aria-labelledby="cmr-summary-copias">
                    <div className="cmr-summary-section__header flex align-items-center gap-2 p-3">
                        <i className="pi pi-copy" aria-hidden="true" />
                        <span id="cmr-summary-copias" className="font-bold text-color">
                            Nuevas Estructuras (Copias)
                        </span>
                        <Tag value={String(estructurasCopiadas.length)} severity="warning" />
                    </div>

                    {estructurasCopiadas.map((est, idx) => {
                        const servicioObj = catalogs.tiposServicio.data.find((s) => s.id === est.tipoServicio);
                        const turnoObj = catalogs.tiposTurno.data.find((t) => t.id === est.turno);
                        return (
                            <div
                                key={est._id}
                                className={`cmr-summary-section__row p-3 flex flex-column gap-3${idx % 2 === 0 ? " cmr-summary-section__row--alt" : ""}`}
                            >
                                <div className="flex align-items-center gap-2">
                                    <div
                                        className="cmr-card-icon flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: 28, height: 28, fontSize: "0.85rem" }}
                                        aria-hidden="true"
                                    >
                                        {idx + 1}
                                    </div>
                                    <span className="font-bold text-color">{est.nombre}</span>
                                    <Tag value="Copia" severity="warning" />
                                </div>

                                <div className="grid pl-4">
                                    <div className="col-12 md:col-3">
                                        <p className="cmr-summary-section__field-label m-0">Horario</p>
                                        <span className="cmr-summary-section__field-value">
                                            {est.horaInicio || "—"} – {est.horaFin || "—"}
                                        </span>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <p className="cmr-summary-section__field-label m-0">Días</p>
                                        <span className="cmr-summary-section__field-value" style={{ fontSize: "0.85rem" }}>
                                            {est.dias.length > 0 ? est.dias.join(", ") : "—"}
                                        </span>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <p className="cmr-summary-section__field-label m-0">Tipo de servicio</p>
                                        <span className="cmr-summary-section__field-value">
                                            {servicioObj?.nombre || est.tipoServicio || "—"}
                                        </span>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <p className="cmr-summary-section__field-label m-0">Turno</p>
                                        <span className="cmr-summary-section__field-value">
                                            {turnoObj?.nombre || est.turno || "—"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </section>
            )}

            <CmrInfoBanner severity="info">
                Al hacer clic en{" "}
                <strong>
                    {showTrazoNotice
                        ? `"${modoEdicion ? "Actualizar" : "Guardar"} y Editar Trazo"`
                        : `"${modoEdicion ? "Actualizar Cotización" : "Guardar Cotización"}"`}
                </strong>{" "}
                se registrarán todos los cambios indicados.
                {" "}Si necesitas ajustar algo, usa el botón <strong>"Anterior"</strong> o los pasos superiores.
            </CmrInfoBanner>
        </>
    );
});

Paso3Confirmar.displayName = "Paso3Confirmar";
export default Paso3Confirmar;
